/**
 * ImportEltJobsModal Component
 *
 * Modal for importing ELT jobs from a JSON export file.
 * Supports file upload, preview, and batch import.
 */

import React, { useState, useRef } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useImportEltJobs } from '../hooks/useImportEltJobs';
import type { EltJobCreateDTO, EltJobImportResponse } from '../../../types/elt-job.types';
import { ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ImportEltJobsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

interface ParsedExportFile {
  version: string;
  exportedAt: string;
  jobs: EltJobCreateDTO[];
}

export const ImportEltJobsModal: React.FC<ImportEltJobsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [parsedFile, setParsedFile] = useState<ParsedExportFile | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [importResult, setImportResult] = useState<EltJobImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: importJobs, isPending } = useImportEltJobs(tenantId, {
    onSuccess: (data) => {
      setImportResult(data);
      setErrorMessage('');
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage('');
    setImportResult(null);
    setFileName(file.name);

    if (!file.name.endsWith('.json')) {
      setErrorMessage('Please select a JSON file');
      setParsedFile(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target?.result as string);

        // Validate basic structure
        if (!content.version || !content.jobs || !Array.isArray(content.jobs)) {
          setErrorMessage('Invalid export file format. Expected a file with "version" and "jobs" fields.');
          setParsedFile(null);
          return;
        }

        if (content.jobs.length === 0) {
          setErrorMessage('Export file contains no jobs.');
          setParsedFile(null);
          return;
        }

        if (content.jobs.length > 50) {
          setErrorMessage('Export file contains more than 50 jobs. Maximum 50 jobs can be imported at once.');
          setParsedFile(null);
          return;
        }

        // Validate each job has required fields
        for (const job of content.jobs) {
          if (!job.eltJobCode || !job.eltJobName) {
            setErrorMessage(`Invalid job definition: each job must have eltJobCode and eltJobName`);
            setParsedFile(null);
            return;
          }
        }

        setParsedFile(content as ParsedExportFile);
      } catch {
        setErrorMessage('Failed to parse JSON file. Please ensure the file is valid JSON.');
        setParsedFile(null);
      }
    };
    reader.onerror = () => {
      setErrorMessage('Failed to read file.');
      setParsedFile(null);
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!parsedFile) return;
    setErrorMessage('');
    importJobs(parsedFile.jobs);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setParsedFile(null);
      setFileName('');
      setImportResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const handleReset = () => {
    setParsedFile(null);
    setFileName('');
    setImportResult(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Import ELT Jobs</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Import Result Summary */}
          {importResult && (
            <div className="space-y-3">
              <Alert variant={importResult.summary.failed === 0 ? 'success' : 'warning'}>
                Import complete: {importResult.summary.created} created, {importResult.summary.failed} failed
                out of {importResult.summary.total} total.
              </Alert>

              {importResult.results.length > 0 && (
                <div className="bg-gray-50 rounded-md border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {importResult.results.map((result, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm font-mono text-gray-900">{result.eltJobCode}</td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                result.status === 'created'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {result.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {result.status === 'created' ? result.eltJobId : result.error}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={handleReset}>
                  Import More
                </Button>
                <Button type="button" onClick={handleClose}>
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* File Upload (shown when no result yet) */}
          {!importResult && (
            <>
              {/* File upload area */}
              {!parsedFile ? (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm font-medium text-gray-900">
                    Click to select a JSON export file
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Accepts .json files exported from ELT Jobs
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <>
                  {/* File selected - show preview */}
                  <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">{fileName}</p>
                        <p className="text-xs text-blue-600">
                          Version {parsedFile.version} &middot; {parsedFile.jobs.length} job{parsedFile.jobs.length !== 1 ? 's' : ''}
                          {parsedFile.exportedAt && ` \u00b7 Exported ${new Date(parsedFile.exportedAt).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleReset}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={isPending}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Preview table */}
                  <div className="bg-gray-50 rounded-md border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Queries</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {parsedFile.jobs.map((job, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">{job.eltJobName}</td>
                            <td className="px-4 py-2 text-sm font-mono text-gray-500">{job.eltJobCode}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{job.queries?.length || 0}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {job.outputFormat?.toUpperCase()}{job.compression && job.compression !== 'none' ? ` (${job.compression})` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleImport} loading={isPending}>
                      Import {parsedFile.jobs.length} Job{parsedFile.jobs.length !== 1 ? 's' : ''}
                    </Button>
                  </div>
                </>
              )}

              {/* Cancel button when no file selected */}
              {!parsedFile && (
                <div className="flex items-center justify-end pt-2">
                  <Button type="button" variant="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
